import { z } from "zod";

import { VibeUsageTypeReadSchema } from "../../../../shared/domain/vibe/usage-types";

/**
 * A UTC calendar day, `YYYY-MM-DD`. The usage series is day-grained and carried
 * as a plain date string rather than an ISO datetime: a UTC-midnight `Date`
 * rendered in a browser west of UTC would show the previous calendar day,
 * silently shifting every bar in the chart by one.
 */
const YmdDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "date must be an ISO calendar day (YYYY-MM-DD)");

/**
 * Query for the usage dashboard read. Both bounds optional: omit for the
 * default trailing window (server-resolved). Bounds are inclusive UTC days.
 */
export const GetVibeUsageQuerySchema = z.object({
  /// Inclusive lower bound (`YYYY-MM-DD`). Omit to default the window.
  from: YmdDateSchema.optional(),
  /// Inclusive upper bound (`YYYY-MM-DD`). Omit → today (UTC).
  to: YmdDateSchema.optional()
});

export type GetVibeUsageQuery = z.infer<typeof GetVibeUsageQuerySchema>;

/** One day's quantity for a single usage type. */
export const VibeUsageDailyPointSchema = z.object({
  date: YmdDateSchema,
  quantity: z.number().nonnegative()
});

export type VibeUsageDailyPointDto = z.infer<typeof VibeUsageDailyPointSchema>;

/**
 * The full daily series for one usage type over the resolved window, plus its
 * window total. `daily` omits empty days — the console draws the axis from the
 * response window and reads a gap as zero.
 */
export const VibeUsageSeriesSchema = z.object({
  usageType: VibeUsageTypeReadSchema,
  total: z.number().nonnegative(),
  daily: z.array(VibeUsageDailyPointSchema)
});

export type VibeUsageSeriesDto = z.infer<typeof VibeUsageSeriesSchema>;

/**
 * Usage read response: one series per `VibeUsageType` (always all types, even at
 * zero) over the resolved inclusive `[from, to]` window. Quantities only — no
 * rated $ at v1 (billing is bundled in Nexus tiers).
 */
export const GetVibeUsageResponseSchema = z.object({
  from: YmdDateSchema,
  to: YmdDateSchema,
  series: z.array(VibeUsageSeriesSchema)
});

export type GetVibeUsageResponse = z.infer<typeof GetVibeUsageResponseSchema>;
